// Date and Time Utility Functions for AAR Guest Management System
import { format, parseISO, isToday, isSunday, isTuesday, startOfDay, endOfDay } from 'date-fns';

/**
 * Format a Date or Firestore Timestamp for display
 * @param {Date|Object} date - Date object or Firestore Timestamp
 * @param {string} formatString - Format string (default: 'MM/dd/yyyy h:mm a')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatString = 'MM/dd/yyyy h:mm a') => {
  if (!date) return '';

  // Handle Firestore Timestamp
  if (date.toDate && typeof date.toDate === 'function') {
    date = date.toDate();
  }

  // Handle ISO string
  if (typeof date === 'string') {
    date = parseISO(date);
  }

  return format(date, formatString);
};

/**
 * Format time only
 * @param {Date|Object} date
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export const formatTime = (date) => {
  return formatDate(date, 'h:mm a');
};

/**
 * Format date only
 * @param {Date|Object} date
 * @returns {string} Formatted date (e.g., "11/09/2025")
 */
export const formatDateOnly = (date) => {
  return formatDate(date, 'MM/dd/yyyy');
};

/**
 * Format full date and time
 * @param {Date|Object} date
 * @returns {string} Formatted date and time (e.g., "Sunday, Nov 9, 2025 at 2:30 PM")
 */
export const formatFullDateTime = (date) => {
  return formatDate(date, 'EEEE, MMM d, yyyy \'at\' h:mm a');
};

/**
 * Check if a date is today
 * @param {Date|Object} date
 * @returns {boolean}
 */
export const isDateToday = (date) => {
  if (!date) return false;

  // Handle Firestore Timestamp
  if (date.toDate && typeof date.toDate === 'function') {
    date = date.toDate();
  }

  return isToday(date);
};

/**
 * Get the current service type based on current day and time
 * @returns {string|null} Service type ID or null
 */
export const getCurrentServiceType = () => {
  const now = new Date();
  const day = now.getDay();
  const hours = now.getHours();

  if (day === 0) { // Sunday
    if (hours >= 7 && hours < 11) return '9am';
    if (hours >= 11 && hours < 15) return '12pm';
    if (hours >= 15 && hours < 18) return '3pm';
  } else if (day === 2) { // Tuesday
    if (hours >= 17 && hours < 21) return 'tuesday_prayer';
  }

  return null;
};

/**
 * Get the volunteer service type based on current day and time
 * Uses wider check-in windows than guest check-in:
 *   Sunday: 8:00-10:00 AM -> 9am, 10:00 AM-12:00 PM -> 12pm, 12:00-2:00 PM -> 3pm
 *   Tuesday: 5:30-6:30 PM -> tuesday_prayer
 *   Other days/times: returns null (check-in still allowed, time recorded as-is)
 * @returns {string|null} Service type ID or null
 */
export const getVolunteerServiceType = () => {
  const now = new Date();
  const day = now.getDay();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const timeInMinutes = hours * 60 + minutes;

  if (day === 0) { // Sunday
    if (timeInMinutes >= 480 && timeInMinutes < 600) return '9am';       // 8:00 AM - 10:00 AM
    if (timeInMinutes >= 600 && timeInMinutes < 720) return '12pm';      // 10:00 AM - 12:00 PM
    if (timeInMinutes >= 720 && timeInMinutes < 840) return '3pm';       // 12:00 PM - 2:00 PM
  } else if (day === 2) { // Tuesday
    if (timeInMinutes >= 1050 && timeInMinutes < 1110) return 'tuesday_prayer'; // 5:30 PM - 6:30 PM
  }

  return null;
};

/**
 * Check if today is a service day (Sunday or Tuesday)
 * @returns {boolean}
 */
export const isServiceDay = () => {
  const now = new Date();
  return isSunday(now) || isTuesday(now);
};

/**
 * Get start and end of today
 * @returns {Object} {start, end}
 */
export const getTodayRange = () => {
  const now = new Date();
  return {
    start: startOfDay(now),
    end: endOfDay(now)
  };
};

/**
 * Get service day name from service type ID
 * @param {string} serviceId
 * @returns {string}
 */
export const getServiceDayName = (serviceId) => {
  if (serviceId === 'tuesday_prayer') return 'Tuesday';
  return 'Sunday';
};

/**
 * Calculate duration between two dates in minutes
 * @param {Date|Object} start
 * @param {Date|Object} end
 * @returns {number} Duration in minutes
 */
export const calculateDuration = (start, end) => {
  if (!start || !end) return 0;

  // Handle Firestore Timestamps
  if (start.toDate) start = start.toDate();
  if (end.toDate) end = end.toDate();

  const diff = end - start;
  return Math.round(diff / 1000 / 60); // Convert to minutes
};

/**
 * Format duration in minutes to readable string
 * @param {number} minutes
 * @returns {string} (e.g., "1h 30m")
 */
export const formatDuration = (minutes) => {
  const totalMins = Math.round(minutes);
  if (totalMins < 60) {
    return `${totalMins}m`;
  }

  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}m`;
};

/**
 * Get date one year ago from today
 * @returns {Date}
 */
export const getOneYearAgo = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date;
};
